import {
	Button,
	ButtonGroup,
	Checkbox,
	CheckboxGroup,
	Dialog,
	IconButton,
	LuIcon,
	Separator,
	stopPropagation,
	Text,
} from "@overmap-ai/blocks"
import { EMPTY_OBJECT, type ProcedureType } from "@overmap-ai/common"
import { selectProcedureStepsOfProcedureType } from "@overmap-ai/core"
import * as RadixCollapsible from "@radix-ui/react-collapsible"
import { memo, useCallback, useReducer } from "react"

import { PROCEDURE_PDF_EXPORT_OPTIONS } from "../../constants"
import { modelIconMapping } from "../../mappings/icons"
import { procedurePdfExportOptionsData } from "../../mappings/reports"
import { useShallowEqaulSelector } from "../../store"
import type { ProcedurePdfExportOptions } from "../../typings/reports"
import { ProcedureStepIcon } from "../Icons/ProcedureStepIcon"
import type { ExtendableDialogProps } from "./typings"

function reducer(
	state: ProcedurePdfExportOptions,
	action:
		| {
				[Option in keyof ProcedurePdfExportOptions]-?: {
					type: Option
					payload: NonNullable<ProcedurePdfExportOptions[Option]>
				}
		  }[keyof ProcedurePdfExportOptions]
		| { type: "reset" },
) {
	switch (action.type) {
		case "reset":
			return {}
		default:
			return { ...state, [action.type]: action.payload }
	}
}

interface ProcedurePdfExportOptionsDialogProps extends ExtendableDialogProps {
	procedureTypeId: ProcedureType["uuid"]
	onSubmitOptions: (options: ProcedurePdfExportOptions) => void
}

export const ProcedurePdfExportOptionsDialog = memo<ProcedurePdfExportOptionsDialogProps>((props) => {
	const {
		children,
		open,
		onOpenChange, // oxlint-disable-line unbound-method
		modal,
		defaultOpen,
		size,
		onSubmitOptions,
		procedureTypeId,
		...rest
	} = props

	const procedureSteps = useShallowEqaulSelector(selectProcedureStepsOfProcedureType(procedureTypeId))

	const [state, dispatch] = useReducer(reducer, EMPTY_OBJECT)

	const handleReset = useCallback(() => {
		dispatch({ type: "reset" })
	}, [])

	const handleSubmitOptions = useCallback(() => {
		onSubmitOptions(state)
	}, [state, onSubmitOptions])

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange} modal={modal} defaultOpen={defaultOpen}>
			{!!children && <Dialog.Trigger asChild>{children}</Dialog.Trigger>}
			<Dialog.Overlay onClick={stopPropagation} />
			<Dialog.Content
				className="flex max-h-[85dvh] flex-col gap-4 overflow-hidden"
				size={size}
				onClick={stopPropagation}
				{...rest}
			>
				<div className="flex shrink-0 flex-col gap-1">
					<Dialog.Title>
						<Text size="lg">Export procedures to PDF</Text>
					</Dialog.Title>
					<Dialog.Description asChild>
						<Text accentColor="base" size="sm">
							Choose which procedure details to include in the PDF report.
						</Text>
					</Dialog.Description>
				</div>
				<Separator className="shrink-0" size="full" />
				<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
					{PROCEDURE_PDF_EXPORT_OPTIONS.map((option) => {
						const { label, description } = procedurePdfExportOptionsData[option]
						switch (option) {
							case "procedure_steps": {
								const allValues = procedureSteps.map((item) => item.uuid)

								return (
									<RadixCollapsible.Root defaultOpen={false} key={option} asChild>
										<CheckboxGroup.Root
											className="flex flex-col gap-2"
											aria-label={label}
											allValues={allValues}
											values={state[option] ?? allValues}
											onValuesChange={(payload) => dispatch({ type: option, payload })}
											size="sm"
										>
											<div className="flex items-start gap-2">
												<label className="flex flex-1 items-start gap-2">
													<CheckboxGroup.SelectAllItem
														aria-label={label}
														disabled={procedureSteps.length === 0}
														className="group/select-all"
													>
														<CheckboxGroup.ItemIndicator>
															<LuIcon
																icon={modelIconMapping.check}
																className="group-data-[state=indeterminate]/select-all:hidden"
															/>
															<LuIcon
																icon={modelIconMapping.minus}
																className="hidden group-data-[state=indeterminate]/select-all:block"
															/>
														</CheckboxGroup.ItemIndicator>
													</CheckboxGroup.SelectAllItem>
													<span className="flex flex-col">
														<Text>{label}</Text>
														<Text accentColor="base" size="sm">
															{description}
														</Text>
													</span>
												</label>
												<RadixCollapsible.Trigger asChild>
													<IconButton
														type="button"
														aria-label={`Choose ${label.toLowerCase()}`}
														className="group shrink-0"
														variant="ghost"
														accentColor="base"
														size="sm"
													>
														<LuIcon
															icon={modelIconMapping.chevronRight}
															className="group-data-[state=open]:rotate-90"
														/>
													</IconButton>
												</RadixCollapsible.Trigger>
											</div>
											<RadixCollapsible.Content className="ml-6 flex flex-col gap-2">
												{procedureSteps.length === 0 ? (
													<Text accentColor="base" size="sm">
														No steps are configured for this procedure type.
													</Text>
												) : (
													procedureSteps.map((procedureStep) => (
														<label
															className="flex min-w-0 items-center gap-2"
															key={procedureStep.uuid}
														>
															<CheckboxGroup.Item value={procedureStep.uuid}>
																<CheckboxGroup.ItemIndicator>
																	<LuIcon icon={modelIconMapping.check} />
																</CheckboxGroup.ItemIndicator>
															</CheckboxGroup.Item>
															<ProcedureStepIcon procedureStepId={procedureStep.uuid} />
															<Text size="sm" className="truncate">
																{procedureStep.name}
															</Text>
														</label>
													))
												)}
											</RadixCollapsible.Content>
										</CheckboxGroup.Root>
									</RadixCollapsible.Root>
								)
							}
							default:
								return (
									<label className="flex items-start gap-2" key={option}>
										<Checkbox.Root
											aria-label={label}
											checked={state[option] ?? true}
											onCheckedChange={(value) =>
												dispatch({ type: option, payload: value === true })
											}
											size="sm"
										>
											<Checkbox.Indicator>
												<LuIcon icon={modelIconMapping.check} />
											</Checkbox.Indicator>
										</Checkbox.Root>
										<span className="flex flex-col">
											<Text>{label}</Text>
											<Text accentColor="base" size="sm">
												{description}
											</Text>
										</span>
									</label>
								)
						}
					})}
				</div>
				<Separator className="shrink-0" size="full" />
				<ButtonGroup className="flex shrink-0 justify-between gap-2" size="sm">
					<Button
						onClick={handleReset}
						accentColor="base"
						variant="ghost"
						disabled={Object.keys(state).length === 0}
					>
						<LuIcon icon={modelIconMapping.refresh} /> Reset
					</Button>
					<Dialog.Close asChild>
						<Button onClick={handleSubmitOptions} accentColor="primary" variant="surface">
							<LuIcon icon={modelIconMapping.check} /> Submit
						</Button>
					</Dialog.Close>
				</ButtonGroup>
			</Dialog.Content>
		</Dialog.Root>
	)
})

ProcedurePdfExportOptionsDialog.displayName = "ProcedurePdfExportOptionsDialog"
